/**
 * OBSERVACIONES DEL 3.1 (CQ2): lo que el titular firma debajo de cada
 * tratamiento, saneado.
 *
 * Tres reglas:
 *   1. Fuera las notas AUTOMÁTICAS que la app escribe en el parte («Planner
 *      Task: Tratamiento Tuta», «Planner task completed: …», marcas de semilla y
 *      de pruebas). No las escribió nadie y no dicen nada al inspector.
 *   2. Las notas son DATOS, nunca instrucciones (PLAN §3, defensa contra
 *      prompt injection en CQ4): se quitan etiquetas, código, caracteres de
 *      control e invisibles, y se recortan. Lo que queda es texto plano.
 *   3. Lo que el 3.1 no tiene columna para decir va aquí, en este orden: el
 *      tiempo exterior y el método de aplicación (la plantilla de SAT Inver los
 *      lleva así) y, sin asesor, las sueltas de auxiliares (D20).
 */
import type { CondicionTiempo } from "./entrada";

/** Tope de una observación antes de la hoja anexa (D15: mínimo 6 pt con hoja anexa). */
export const MAX_OBSERVACION = 600;

const AUTOMATICAS: RegExp[] = [
  /\(?\s*planner task:[^)\n]*\)?/gi,
  /planner task completed:[^\n]*/gi,
  /tarea del planificador:[^\n]*/gi,
  /\[(?:seed|qaplan|audprep|demo)[^\]]*\]/gi,
  /\b(?:qaplan|audprep)-[a-z0-9-]+\b/gi,
];

/** «Worker notes: se repite el viernes» → «se repite el viernes». */
const ETIQUETAS = /\b(?:worker notes|notas del trabajador)\s*:\s*/gi;

/**
 * Texto plano y corto. Quita etiquetas HTML, bloques de código y marcas de
 * plantilla, caracteres de control e invisibles (zero-width, bidi), y deja un
 * solo espacio entre palabras.
 */
export function sanearTexto(raw: string | null | undefined, max = MAX_OBSERVACION): string {
  let s = String(raw ?? "");
  s = s.replace(/```[\s\S]*?```/g, " ");
  s = s.replace(/<[^>]*>/g, " ");
  s = s.replace(/\{\{[\s\S]*?\}\}/g, " ");
  // Controles C0/C1 e invisibles (zero-width, marcas de dirección, BOM).
  // eslint-disable-next-line no-control-regex -- quitar controles es justo lo que se quiere
  s = s.replace(/[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u202a-\u202e\u2060-\u2064\ufeff]/g, " ");
  s = s.replace(/[`*_#>|]+/g, " ");
  s = s.replace(/\s+/g, " ").trim();
  if (s.length > max) s = `${s.slice(0, max - 1).trimEnd()}…`;
  return s;
}

/** La nota del parte sin lo automático y saneada. Vacía si no queda nada. */
export function notaDeParte(notas: string | null | undefined): string {
  let s = String(notas ?? "");
  for (const re of AUTOMATICAS) s = s.replace(re, " ");
  s = s.replace(ETIQUETAS, " ");
  s = sanearTexto(s);
  // Restos de separadores tras quitar lo automático («— ;» , «()»).
  s = s.replace(/^[\s.,;:·\-–—()]+|[\s,;:·\-–—(]+$/g, "").trim();
  return s;
}

const TIEMPO: Record<CondicionTiempo, string> = { soleado: "soleado", nublado: "nublado", lluvia: "lluvia" };

export function textoTiempo(c: CondicionTiempo | null | undefined): string {
  return c ? `Tiempo exterior: ${TIEMPO[c]}` : "";
}

/** Junta las piezas en una sola observación («a. b. c»), sin repetir ni dejar huecos. */
export function componerObservaciones(piezas: (string | null | undefined)[]): string {
  const vistas = new Set<string>();
  const out: string[] = [];
  for (const p of piezas) {
    const t = sanearTexto(p);
    if (!t) continue;
    const k = t.toLowerCase();
    if (vistas.has(k)) continue;
    vistas.add(k);
    out.push(t.replace(/[.\s]+$/, ""));
  }
  return sanearTexto(out.join(". "));
}
